import { memo } from 'react';
import { formatDistanceToNow, isPast, isToday, isTomorrow } from 'date-fns';
import { AlertCircle, Calendar, Clock, Flag, GripVertical, MessageSquare, MoreHorizontal, Paperclip, Workflow } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TaskAssigneesCell } from '@/components/tasks/assignees-display';
import { formatDisplayDate } from '@/lib/date-format';
import { cn, getContrastTextColor } from '@/lib/utils';
import { getEffectiveLastUpdated } from '@/lib/task-last-updated';
import type { Task } from '@/types';
import type { CustomFieldDefinition } from '@/lib/api';

const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#3b82f6',
};

function formatTaskRef(taskId: string): string {
  const compact = taskId.replace(/-/g, '').slice(0, 8).toUpperCase();
  return `#${compact}`;
}

function formatCustomFieldCellValue(def: CustomFieldDefinition, value: unknown): string {
  if (value === null || value === undefined) return '—';
  switch (def.type) {
    case 'date':
      try {
        return formatDisplayDate(value as string);
      } catch {
        return String(value);
      }
    case 'checkbox':
      return value ? 'Yes' : 'No';
    case 'select': {
      const options = (def.config?.options as Array<{ value: string; label?: string }>) ?? [];
      const option = options.find((item) => item.value === value);
      return option?.label ?? String(value);
    }
    default:
      return String(value);
  }
}

function StatusBadge({ task }: { task: Task }) {
  const bgColor = task.status?.color || '#6b7280';
  const textColor = getContrastTextColor(bgColor);
  return (
    <Badge
      variant="secondary"
      className="h-6 px-2 text-[11px] font-medium"
      style={{
        backgroundColor: bgColor,
        color: textColor,
        borderColor: 'transparent',
      }}
    >
      {task.status?.name ?? 'No status'}
    </Badge>
  );
}

function TagPill({ name, color }: { name: string; color?: string | null }) {
  const bgColor = color || '#6b7280';
  const textColor = getContrastTextColor(bgColor);
  return (
    <Badge
      variant="secondary"
      className="h-5 px-1.5 text-[10px] font-medium"
      style={{
        backgroundColor: bgColor,
        color: textColor,
        borderColor: 'transparent',
      }}
    >
      {name}
    </Badge>
  );
}

function TaskMetaIndicators({ task }: { task: Task }) {
  const comments = task.commentCount ?? task._count?.comments ?? 0;
  const attachments = task.attachmentCount ?? task._count?.attachments ?? 0;
  const subtasks = task.subtaskCount ?? task._count?.subtasks ?? task.subtasks?.length ?? 0;

  return (
    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <MessageSquare className="h-3 w-3" />
        {comments}
      </span>
      <span className="inline-flex items-center gap-1">
        <Paperclip className="h-3 w-3" />
        {attachments}
      </span>
      <span className="inline-flex items-center gap-1">
        <Workflow className="h-3 w-3" />
        {subtasks}
      </span>
    </div>
  );
}

function AssigneeCell({ task }: { task: Task }) {
  return <TaskAssigneesCell task={task} variant="compact" className="max-w-full" />;
}

interface TaskListRowProps {
  task: Task;
  onClick: () => void;
  customColumns: CustomFieldDefinition[];
  customValues: Record<string, unknown>;
  gridTemplateColumns: string;
}

export const TaskListRow = memo(function TaskListRow({
  task,
  onClick,
  customColumns,
  customValues,
  gridTemplateColumns,
}: TaskListRowProps) {
  const isOverdue = task.dueDate && isPast(new Date(task.dueDate)) && !task.status?.isDoneState;
  const isDueToday = task.dueDate && isToday(new Date(task.dueDate));
  const isDueTomorrow = task.dueDate && isTomorrow(new Date(task.dueDate));
  const dueDateLabel = task.dueDate
    ? isDueToday
      ? 'Today'
      : isDueTomorrow
        ? 'Tomorrow'
        : formatDisplayDate(task.dueDate)
    : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{ gridTemplateColumns }}
      className={cn(
        'w-full grid items-center gap-2 px-3 py-1.5 text-left group cursor-pointer',
        'border-b border-border hover:bg-accent/40 transition-colors',
        'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring'
      )}
    >
      <div className="flex items-start gap-2 min-w-0">
        <GripVertical className="h-4 w-4 mt-[2px] text-muted-foreground/70 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-mono text-muted-foreground shrink-0">
              {formatTaskRef(task.id)}
            </span>
            <span className="text-sm leading-tight truncate">{task.title}</span>
            {isOverdue && <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />}
          </div>
          <div className="mt-1 flex items-center gap-2 min-w-0">
            {task.tags?.slice(0, 3).map((tag) => (
              <TagPill key={tag.id} name={tag.name} color={tag.color} />
            ))}
            {task.tags && task.tags.length > 3 ? (
              <span className="text-[10px] text-muted-foreground">+{task.tags.length - 3}</span>
            ) : null}
            <TaskMetaIndicators task={task} />
          </div>
        </div>
      </div>

      <StatusBadge task={task} />

      <div className="min-w-0">
        <AssigneeCell task={task} />
      </div>

      <div className="flex items-center">
        {dueDateLabel ? (
          <span
            className={cn(
              'text-xs flex items-center gap-1',
              isOverdue && 'text-destructive',
              isDueToday && !isOverdue && 'text-yellow-500',
              !isOverdue && !isDueToday && 'text-muted-foreground'
            )}
          >
            <Calendar className="h-3 w-3" />
            {dueDateLabel}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground/60">—</span>
        )}
      </div>

      <div className="flex items-center">
        {task.priority ? (() => {
          const bgColor = PRIORITY_COLORS[task.priority] || '#6b7280';
          const textColor = getContrastTextColor(bgColor);
          return (
            <Badge
              variant="secondary"
              className="text-[10px] h-5 px-1.5 capitalize font-medium"
              style={{
                backgroundColor: bgColor,
                color: textColor,
                borderColor: 'transparent',
              }}
            >
              <Flag className="h-2.5 w-2.5 mr-1" />
              {task.priority}
            </Badge>
          );
        })() : (
          <span className="text-xs text-muted-foreground/60">—</span>
        )}
      </div>

      <div className="flex items-center">
        {(() => {
          const effectiveUpdatedAt = getEffectiveLastUpdated(task);
          return effectiveUpdatedAt ? (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(effectiveUpdatedAt, { addSuffix: true })}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/60">—</span>
          );
        })()}
      </div>

      {customColumns.map((def) => (
        <div key={def.id} className="min-w-0 truncate text-xs text-muted-foreground">
          {formatCustomFieldCellValue(def, customValues[def.id])}
        </div>
      ))}

      <div className="w-8 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>
    </div>
  );
});
